import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrintPreview = ({ 
  previewData, 
  onSaveCalculation, 
  onStartPrint,
  isValid 
}) => {
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const resourceItems = [
    {
      label: 'Material necessário',
      value: `${previewData?.materialNeeded}g`,
      icon: 'Package',
      color: 'text-primary'
    },
    {
      label: 'Consumo energético',
      value: `${previewData?.powerConsumption}kWh`,
      icon: 'Zap',
      color: 'text-warning'
    },
    {
      label: 'Horas de impressão',
      value: formatTime(previewData?.printTime),
      icon: 'Clock',
      color: 'text-secondary'
    },
    {
      label: 'Camadas estimadas',
      value: `${previewData?.layers} camadas`,
      icon: 'Layers',
      color: 'text-accent'
    }
  ];

  return (
    <motion.div 
      className="bg-card rounded-2xl border border-border p-6"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Eye" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Preview da Impressão</h3>
          <p className="text-sm text-muted-foreground">Resumo do projeto e recursos</p>
        </div>
      </div>
      {!isValid ? (
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            Configure todos os parâmetros para ver o preview
          </p>
        </div>
      ) : (
        <>
          {/* Project Summary */}
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {formatCurrency(previewData?.totalCost)}
                </div>
                <div className="text-sm text-muted-foreground">Custo Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">
                  {formatTime(previewData?.printTime)}
                </div>
                <div className="text-sm text-muted-foreground">Tempo Estimado</div>
              </div>
            </div>
          </div>

          {/* Resource Consumption */}
          <div className="space-y-3 mb-6">
            <h4 className="font-medium text-foreground mb-3">Consumo de Recursos</h4>
            {resourceItems?.map((item, index) => (
              <motion.div
                key={item?.label}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <div className="flex items-center space-x-3">
                  <Icon name={item?.icon} size={16} className={item?.color} />
                  <span className="text-sm text-foreground">{item?.label}</span>
                </div>
                <span className="text-sm font-medium text-foreground">{item?.value}</span>
              </motion.div>
            ))}
          </div>

          {/* Completion Estimate */}
          <div className="bg-muted/30 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Previsão de Conclusão</span>
            </div>
            <div className="text-lg font-semibold text-foreground">
              {new Date(Date.now() + previewData.printTime * 60 * 60 * 1000)?.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Considerando início imediato
            </div>
          </div>

          {/* Stock Validation */}
          <div className="bg-muted/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={previewData?.hasEnoughStock ? "CheckCircle" : "AlertTriangle"} 
                  size={16} 
                  className={previewData?.hasEnoughStock ? "text-success" : "text-warning"} 
                />
                <span className="text-sm font-medium text-foreground">Status do Estoque</span>
              </div>
              <span className={`text-sm font-medium ${previewData?.hasEnoughStock ? "text-success" : "text-warning"}`}>
                {previewData?.hasEnoughStock ? "Disponível" : "Insuficiente"}
              </span>
            </div>
            {!previewData?.hasEnoughStock && (
              <div className="mt-2 text-sm text-warning">
                Faltam {previewData?.materialNeeded - previewData?.availableStock}g de filamento
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={onSaveCalculation}
              iconName="Save"
              iconPosition="left"
              className="flex-1"
            >
              Salvar Cálculo
            </Button>
            
            <Button
              variant="default"
              onClick={onStartPrint}
              disabled={!previewData?.hasEnoughStock}
              iconName="Play"
              iconPosition="left"
              className="flex-1"
            >
              Iniciar Impressão
            </Button>
          </div>

          {!previewData?.hasEnoughStock && (
            <div className="mt-3 text-center">
              <Button
                variant="secondary"
                size="sm"
                iconName="Package"
                iconPosition="left"
              >
                Gerenciar Estoque
              </Button>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default PrintPreview;